import { Formik, FormikBag, useFormikContext } from 'formik';
import * as React from 'react';
import { toNativePromise } from '~commonInfra/utils/QUtils';
import { ByteSizeConversionFactor } from '~commonInfra/utils/SizeFilter';
import { useSettingsCoordinator } from '~settings-coordinator/SettingsCoordinatorHook';

import { BrowserSupport } from '@tableau/browser-support';
import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget } from '@tableau/widgets';

import { getAppStore } from '../../../app/AppStore';
import { NavPanelLogo } from '../../../app/layout/NavPanelLogo';
import { siteUrlsSelector } from '../../../app/selectors/SiteUrlsSelectors';
import { Messages } from '../../../codegen/Localize';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import {
    getSessionManagementServiceInstance
} from '../../../sessionManagement/SessionManagementService';
import { CommonErrorCode, OnlineErrorCode } from '../../../ts/helpers/ErrorCodes';
import { FileDragAndDrop } from '../../../uploadContent/components/FileDragAndDrop';
import { SettingsSection } from '../../components/common/SettingsSection';
import {
    componentRegistryEntry
} from '../../general-settings/components/SettingsComponentRegistryFunctions';
import { LogoHelper } from '../helpers/LogoHelper';
import { ILogo, LogoService, LogoType } from '../services/LogoService';
import { FormStyles } from '../styles/UpdateSiteLogoFormStyles';

const SUPPORTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif'];
const MAX_IMAGE_SIZE = ByteSizeConversionFactor.ONE_MB * 2;

export namespace Fields {
  export const COMPACT_LOGO_FIELD = 'compactLogo';
  export const COMPACT_LOGO_FILE_FIELD = 'compactLogoFile';
  export const GENERAL_FIELD = 'general';
  export const LOGO_FIELD = 'logo';
  export const LOGO_FILE_FIELD = 'logoFile';
  export const RESET_TO_DEFAULT_FIELD = 'resetToDefault';
  export const SEPARATE_COMPACT_LOGO_FIELD = 'separateCompactLogo';
}

export namespace TestIds {
  export const COMPACT_LOGO_PREVIEW = 'compact-logo-drop-target';
  export const DRAG_AND_DROP_PROMPT = 'drag-and-drop-prompt';
  export const ERROR_MESSAGE = 'error-message';
  export const HEADER_LOGO_PREVIEW = 'header-logo-drop-target';
  export const LOGO_FILENAME_TEXT = 'logo-filename';
  export const RESET_TO_DEFAULT = 'reset-to-default';
  export const SEPARATE_COMPACT_LOGO_CHECKBOX = 'separate-image-for-compact-logo';
  export const SITE_LOGO_SECTION = 'site-logo-section';
  export const UPLOAD_HEADER_LOGO_FILE = 'upload-file-for-header-logo';
  export const UPLOAD_COMPACT_LOGO_FILE = 'upload-file-for-compact-logo';
}

export function UpdateSiteLogoSection(): JSX.Element {
  if (LogoService.displaySiteLogoCustomization()) {
    return <UpdateSiteLogoForm
      isDragAndDropEnabled={BrowserSupport.SupportsDragAndDropEvents() &&
        BrowserSupport.SupportsFormData() &&
        !BrowserSupport.IsTouch()}
      isPreviewEnabled={BrowserSupport.SupportsFileReader()} />;
  } else {
    return null;
  }
}

export type UpdateSiteLogoFormProps = {
  readonly isDragAndDropEnabled: boolean;
  readonly isPreviewEnabled: boolean;
}

export type FormikLogoValues = {
  logoFile: File;
  logo: ILogo;
  separateCompactLogo: boolean;
  compactLogoFile: File;
  compactLogo: ILogo;
  resetToDefault: boolean;
}

// Must initialize values to 'null' so that the property has a value that can
// be tracked by formik (aka don't use 'undefined').
function getInitialFormikValues(): FormikLogoValues {
  return {
    logoFile: null,
    logo: LogoService.getNavPanelLogo(),
    separateCompactLogo: LogoService.hasCustomCompactLogo(),
    compactLogoFile: null,
    compactLogo: LogoService.getCompactLogo(),
    resetToDefault: false
  };
}

export function UpdateSiteLogoForm(props: UpdateSiteLogoFormProps) {
  const initialFormikValues = React.useMemo(getInitialFormikValues, []);
  return <Formik initialValues={initialFormikValues}
                 enableReinitialize={false}
                 onSubmit={submitSiteLogoForm}>
    <UpdateSiteLogoComponent {...props}/>
  </Formik>;
}


export const handleFormSubmitResult = async (results: IResult[], actions: FormikBag<FormikLogoValues, {}>): Promise<void> => {
  for (const result of results) {
    if (result?.errors?.length) {
      switch (result.errors[0].code) {
        case CommonErrorCode.FileSizeTooLarge:
          actions.setFieldError(Fields.GENERAL_FIELD, Messages.uploadSiteLogoErrorFileLargerThan2Megabytes());
          break;
        case OnlineErrorCode.SiteCustomizationUnavailable:
          actions.setFieldError(Fields.GENERAL_FIELD, Messages.actionNotAvailable());
          break;
        case OnlineErrorCode.InvalidImage:
        case OnlineErrorCode.ImageProcessingError:
        case OnlineErrorCode.StorageException:
        default:
          actions.setFieldError(Fields.GENERAL_FIELD, Messages.uploadSiteLogoErrorSaveError());
          break;
      }
      return Promise.reject();
    }
  }

  return toNativePromise(getSessionManagementServiceInstance().refreshSessionInfo() // Fetch the updated logo
    .then(() => {
      const values = getInitialFormikValues(); // same values use in the initialValues block
      actions.resetForm({values});
    }));
};

export const submitSiteLogoForm = (values: FormikLogoValues, actions: FormikBag<FormikLogoValues, {}>): Promise<void> => {
  let updatePromise: Promise<IResult[]>;
  if (!values.resetToDefault) {
    // Only update the logos that have changed
    const shouldUpdateCompact = !!values.compactLogoFile ||
        (LogoService.hasCustomCompactLogo() && !values.separateCompactLogo);
    updatePromise = Promise.all([
      values.logoFile && LogoService.updateSiteLogo(values.logoFile, 'header'),
      shouldUpdateCompact && LogoService.updateSiteLogo(values.compactLogoFile, 'compact')
    ]);
  } else {
    updatePromise = Promise.all([
      LogoService.updateSiteLogo(null, 'header'),
      LogoService.updateSiteLogo(null, 'compact')
    ]);
  }
  return updatePromise.then((results: IResult[]) => handleFormSubmitResult(results, actions));
};


export function UpdateSiteLogoComponent(props: UpdateSiteLogoFormProps): JSX.Element {
  const formik = useFormikContext<FormikLogoValues>();
  const updateSiteLogoBackgroundColor =
    (formik.errors as any).general ? FormStyles.errorStateBackgroundColor : FormStyles.defaultStateBackgroundColor;

  // wire the current formik form to the settings coordinator
  useSettingsCoordinator();

  const handleResetToDefaultClick = (): void => {
    formik.setFieldError(Fields.GENERAL_FIELD, undefined);
    formik.setValues({
        logoFile: null,
        compactLogoFile: null,
        separateCompactLogo: false,
        logo: LogoService.getDefaultHeaderLogo(),
        compactLogo: LogoService.getDefaultCompactLogo(),
        resetToDefault: true
    });
  };

  const onFileAddedOrChanged = async (file: File, logoType: LogoType) => {
    // If the user had clicked "reset to default" but then tries uploading a new logo, reset the state of the form to
    // the logos that are currently saved before applying the new logo.
    if (formik.values.resetToDefault) {
      if (logoType === 'compact') {
        formik.setFieldValue(Fields.LOGO_FIELD, LogoService.getNavPanelLogo());
      }
      formik.setFieldValue(Fields.RESET_TO_DEFAULT_FIELD, false);
    }

    if (file) {
      if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
        formik.setFieldError(Fields.GENERAL_FIELD, Messages.uploadSiteLogoErrorFileTypeNotSupported());
      } else if (file.size && file.size > MAX_IMAGE_SIZE) {
        formik.setFieldError(Fields.GENERAL_FIELD, Messages.uploadSiteLogoErrorFileLargerThan2Megabytes());
      } else {
        formik.setFieldError(Fields.GENERAL_FIELD, undefined);

        if (logoType === 'compact') {
          formik.setFieldValue(Fields.COMPACT_LOGO_FILE_FIELD, file);
        } else {
          formik.setFieldValue(Fields.LOGO_FILE_FIELD, file);
        }

        if (props.isPreviewEnabled) {
          if (logoType === 'compact') {
            await updateCompactLogo(file);
          } else {
            await updateLogo(file);
          }
        }
      }
    }
  };

  const updateLogo = async (file: File) => {
    try {
      const logo = await LogoHelper.generatePreviewLogo(file, 'header');
      formik.setFieldValue(Fields.LOGO_FIELD, logo);

      if (!formik.values.separateCompactLogo ||
          (formik.values.separateCompactLogo && !formik.values.compactLogoFile && !LogoService.hasCustomCompactLogo())) {
        const compactLogo = await LogoHelper.generatePreviewLogo(file, 'compact');
        formik.setFieldValue(Fields.COMPACT_LOGO_FIELD, compactLogo);
      }
    } catch (err) {
      formik.setFieldError(Fields.GENERAL_FIELD, err.message);
    }
  };

  const updateCompactLogo = async (file: File) => {
    try {
      const compactLogo = await LogoHelper.generatePreviewLogo(file, 'compact');
      formik.setFieldValue(Fields.COMPACT_LOGO_FIELD, compactLogo);
    } catch (err) {
      formik.setFieldError(Fields.GENERAL_FIELD, err.message);
    }
  };

  const toggleSeparateCompactLogo = () => {
    const setToChecked = !formik.values.separateCompactLogo;
    formik.setFieldValue(Fields.SEPARATE_COMPACT_LOGO_FIELD, setToChecked);
    if (!setToChecked) {
      updateCompactLogoAsNotSeparate();
    } else {
      updateCompactLogoAsSeparate();
    }
  };

  const updateCompactLogoAsNotSeparate = () => {
    formik.setFieldValue(Fields.COMPACT_LOGO_FILE_FIELD, null);
    if (LogoService.hasCustomHeaderLogo() || formik.values.logoFile) {
      const scaledHeaderLogo = LogoService.getScaledLogo(formik.values.logo, 'compact');
      formik.setFieldValue(Fields.COMPACT_LOGO_FIELD, scaledHeaderLogo);
    } else {
      formik.setFieldValue(Fields.COMPACT_LOGO_FIELD, LogoService.getDefaultCompactLogo());
    }
  };

  const updateCompactLogoAsSeparate = () => {
    if (LogoService.hasCustomCompactLogo() || formik.values.compactLogoFile) {
      if (formik.values.logoFile) {
        const scaledHeaderLogo = LogoService.getScaledLogo(formik.values.logo, 'compact');
        formik.setFieldValue(Fields.COMPACT_LOGO_FIELD, scaledHeaderLogo);
      } else {
        formik.setFieldValue(Fields.COMPACT_LOGO_FIELD, LogoService.getCompactLogo());
      }
    }
  };

  const fileInputChanged = React.useCallback(
    async (fileInputElement: HTMLInputElement, logoType: LogoType) => {
      if (fileInputElement) {
        // File object is not accessible in all browsers
        const file = fileInputElement.files && fileInputElement.files[0];
        await onFileAddedOrChanged(file, logoType);
      } else if (!formik.values.resetToDefault) {
        // User clicked 'cancel' in OS file selection
        formik.resetForm();
      }
    }, [formik.values.resetToDefault, formik.values.separateCompactLogo, formik.values.compactLogoFile]);

  const errorMessage = (formik.errors as any).general;
  return <>
    <SettingsSection
      description={Messages.uploadSiteLogoSettingsMsg()}
      testId={TestIds.SITE_LOGO_SECTION}
      title={Messages.siteLogo()}
    >
      <div style={{...FormStyles.updateLogoRegionStyles, backgroundColor: updateSiteLogoBackgroundColor}}>
        {props.isDragAndDropEnabled &&
          <div style={FormStyles.alignFormContent} data-tb-test-id={TestIds.DRAG_AND_DROP_PROMPT}>
            {Messages.uploadSiteLogoInstructionsDragAndDrop()}
          </div>}
        <FormControls
          handleResetToDefaultClick={handleResetToDefaultClick}
          handleSelectFileClick={fileInputChanged}
          resetToDefault={formik.values.resetToDefault} />
        <div style={FormStyles.previewTextPadding}>{Messages.preview()}</div>
        {!errorMessage && props.isPreviewEnabled &&
        <div style={FormStyles.centerJustifyContent}>
          <LogoPreview
            compactLogo={formik.values.compactLogo}
            handleFileDrop={fileInputChanged}
            logo={formik.values.logo}
            logoType={'header'} />
          <LogoPreview
            compactLogo={formik.values.compactLogo}
            handleFileDrop={fileInputChanged}
            logo={formik.values.logo}
            logoType={'compact'} />
        </div>}
        {errorMessage &&
          <div
            data-tb-test-id={TestIds.ERROR_MESSAGE}
            style={FormStyles.errorTextStyles}>
              {errorMessage}
            </div>}
        {(!props.isPreviewEnabled && !!formik.values.logoFile?.name) &&
          <div
            data-tb-test-id={TestIds.LOGO_FILENAME_TEXT}
            style={FormStyles.displayFilenameTextStyles}
            title={formik.values.logoFile.name}>
              {formik.values.logoFile.name}
          </div>}
        <CompactLogoControls
          separateCompactLogo={formik.values.separateCompactLogo}
          handleSelectFileClick={fileInputChanged}
          toggleSeparateCompactLogo={toggleSeparateCompactLogo}/>
      </div>
    </SettingsSection>
  </>;
}


type FormControlsProps = {
  readonly handleResetToDefaultClick: () => void;
  readonly handleSelectFileClick: SelectFileButtonProps['handleSelectFileClick'];
  readonly resetToDefault: boolean;
}

function FormControls(props: FormControlsProps): JSX.Element {
  let logoFileInput = React.useRef<HTMLInputElement>(null);

  const usingCustomLogo = () => {
    return (LogoService.isUsingCustomSiteLogo() || LogoService.hasCustomCompactLogo());
  };

  return <div style={FormStyles.alignFormContent}>
    {usingCustomLogo() &&
      <ButtonWidget
        buttonType={ButtonType.Outline}
        handleClick={props.handleResetToDefaultClick}
        disabled={props.resetToDefault}
        style={FormStyles.buttonSpacing}
        testId={TestIds.RESET_TO_DEFAULT}>
        {Messages.resetToDefault_action()}
      </ButtonWidget>}
    <SelectFileButton
      handleSelectFileClick={props.handleSelectFileClick}
      logoType={'header'}
      inputFileRef={logoFileInput}
      testId={TestIds.UPLOAD_HEADER_LOGO_FILE}/>
  </div>;
}


type LogoPreviewProps = {
  readonly handleFileDrop: (fileInputElement: HTMLInputElement, logoType: LogoType) => void;
  readonly logo: ILogo;
  readonly compactLogo: ILogo;
  readonly logoType: LogoType;
}

function LogoPreview(props: LogoPreviewProps): JSX.Element {
  const isCompactLogo = props.logoType === 'compact';
  const [label, dropTargetStyle] = isCompactLogo ?
    [Messages.leftNavCollapsed(), FormStyles.compactLogoPreviewClass] :
    [Messages.leftNavExpanded(), FormStyles.headerLogoPreviewClass];

  return <>
    <div style={FormStyles.previewDropTargetPadding}>
      <FileDragAndDrop
        handleDrop={(event: React.ChangeEvent<HTMLInputElement>) => props.handleFileDrop(event.target, props.logoType)}
        containerClassName={''}
        defaultStateClassName={dropTargetStyle}
        activeStateClassName={dropTargetStyle}
        onDragClassName={FormStyles.dropTargetHighlightingClass}
        testId={isCompactLogo ? TestIds.COMPACT_LOGO_PREVIEW : TestIds.HEADER_LOGO_PREVIEW}>
        <NavPanelLogo
          compactLogo={props.compactLogo}
          collapsed={isCompactLogo}
          disableLink={true}
          logo={props.logo}
          siteUrls={siteUrlsSelector(getAppStore().getState())} />
      </FileDragAndDrop>
      <div>{label}</div>
    </div>
    </>;
}


type CompactLogoControlsProps = {
  readonly separateCompactLogo: boolean;
  readonly handleSelectFileClick: SelectFileButtonProps['handleSelectFileClick'];
  readonly toggleSeparateCompactLogo: () => void;
}

function CompactLogoControls(props: CompactLogoControlsProps): JSX.Element {
  let compactLogoFileInput = React.useRef<HTMLInputElement>(null);

  return <>
    <div style={FormStyles.alignFormContent}>
      <CheckBoxWithLabelWidget
        checked={!!props.separateCompactLogo}
        containerStyle={FormStyles.pointerCursor}
        handleChange={props.toggleSeparateCompactLogo}
        label={Messages.uploadSiteLogoChooseDifferentForCompact()}
        testId={TestIds.SEPARATE_COMPACT_LOGO_CHECKBOX} />
    </div>
    {props.separateCompactLogo &&
      <div style={FormStyles.alignFormContent}>
        <SelectFileButton
          handleSelectFileClick={props.handleSelectFileClick}
          logoType={'compact'}
          inputFileRef={compactLogoFileInput}
          testId={TestIds.UPLOAD_COMPACT_LOGO_FILE}/>
      </div>}
  </>;
}


type SelectFileButtonProps = {
  readonly handleSelectFileClick: (fileInputElement: HTMLInputElement, logoType: LogoType) => void;
  readonly logoType: LogoType;
  readonly inputFileRef: React.MutableRefObject<HTMLInputElement>;
  readonly testId: string;
}

function SelectFileButton(props: SelectFileButtonProps): JSX.Element {
  return <ButtonWidget
      buttonType={ButtonType.Outline}
      onClick={() => props.inputFileRef.current.click()}
      style={FormStyles.buttonSpacing}
      testId={props.testId}>
      {Messages.selectFile()}
      <input
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          props.handleSelectFileClick(e.target, props.logoType);
        }}
        ref={props.inputFileRef}
        style={FormStyles.doNotDisplay}
        type={'file'} />
    </ButtonWidget>;
}


/* for registering the component in settings registry */
export const UpdateSiteLogoFormSpecs = componentRegistryEntry({
  name: 'UpdateSiteLogoSection',
  component: UpdateSiteLogoSection
});
